import { makeAutoObservable, observable, runInAction } from "mobx";
import type { RootStore } from "./root.store";
import type { TRequestStatus } from "@/types/request-status";
import { REQUEST_STATUS_GROUPS } from "@/constants/request-statuses";
import type { TRequestStatusGroup } from "@/constants/request-statuses";
import type {
  TRequestStatusCreateData,
  TRequestStatusUpdateData,
} from "@/services/request-status.service";
import { isPermissionError } from "@/lib/reports/is-permission-error";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields } from "@/lib/changed-fields";

export type TGroupedRequestStatuses = Record<TRequestStatusGroup, TRequestStatus[]>;

// Stable "no cache entry yet" sentinel — see the identical constant in
// projects-v3.store.ts for why a literal `[]` here is a bug, not a style nit:
// a caller (the requests list widget) feeding this into a memo dependency
// array sees a "changed" value on every render while the read stays 403'd.
const EMPTY_STATUSES: TRequestStatus[] = [];

function emptyGroups(): TGroupedRequestStatuses {
  return REQUEST_STATUS_GROUPS.reduce((groups, group) => {
    groups[group] = [];
    return groups;
  }, {} as TGroupedRequestStatuses);
}

export class RequestStatusesStore {
  isLoading = false;
  fetchError: Error | null = null;
  isMutating = false;
  mutatingStateId: string | null = null;
  statesByWorkspace: Map<string, TRequestStatus[]> = new Map();

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      {
        rootStore: false,
        statesByWorkspace: observable,
      } as never,
      { autoBind: true },
    );
  }

  getStatuses(workspaceSlug: string): TRequestStatus[] {
    return this.statesByWorkspace.get(workspaceSlug) ?? EMPTY_STATUSES;
  }

  getGroupedStatuses(workspaceSlug: string): TGroupedRequestStatuses {
    const groups = emptyGroups();
    const states = this.getStatuses(workspaceSlug);
    for (const state of states) {
      const bucket = groups[state.group];
      if (bucket) bucket.push(state);
    }
    for (const group of REQUEST_STATUS_GROUPS) {
      groups[group] = [...groups[group]].sort((a, b) => a.sequence - b.sequence);
    }
    return groups;
  }

  private setStatuses(workspaceSlug: string, states: TRequestStatus[]): void {
    this.statesByWorkspace.set(workspaceSlug, states);
  }

  async fetchStatuses(slug: string): Promise<TRequestStatus[]> {
    this.isLoading = true;
    this.fetchError = null;
    try {
      const states = await this.rootStore.requestStatusService.list(slug);
      runInAction(() => {
        this.setStatuses(slug, states);
      });
      return states;
    } catch (err) {
      runInAction(() => {
        this.fetchError = err as Error;
      });
      // A 403 here is an expected degradation for a role without the
      // settings-read permission this list needs (e.g. a requester-only
      // Standard role, NG-1879) — the requests list still renders, just
      // without status filtering. Anything else is a real failure.
      if (isPermissionError(err)) {
        console.warn("[request-statuses] forbidden for this role", { slug, err });
      } else {
        console.error("Failed to fetch request statuses", { slug, err });
      }
      return [];
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async createStatus(
    slug: string,
    data: TRequestStatusCreateData,
    source: AnalyticsSource = "page",
  ): Promise<TRequestStatus> {
    this.isMutating = true;
    try {
      const created = await this.rootStore.requestStatusService.create(slug, data);
      runInAction(() => {
        this.setStatuses(slug, [...this.getStatuses(slug), created]);
      });
      track("request_state_created", { state_id: created.id, group: created.group, source });
      return created;
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async updateStatus(
    slug: string,
    stateId: string,
    data: TRequestStatusUpdateData,
    source: AnalyticsSource = "page",
  ): Promise<TRequestStatus> {
    const snapshot = this.getStatuses(slug);
    const target = snapshot.find((s) => s.id === stateId);
    // Read before the optimistic patch: `fields` is what this save changed.
    const fields = changedFields(data, target);
    runInAction(() => {
      this.mutatingStateId = stateId;
      if (target) {
        this.setStatuses(
          slug,
          snapshot.map((s) => (s.id === stateId ? ({ ...s, ...data } as TRequestStatus) : s)),
        );
      }
    });
    try {
      const updated = await this.rootStore.requestStatusService.update(slug, stateId, data);
      runInAction(() => {
        this.setStatuses(
          slug,
          this.getStatuses(slug).map((s) => (s.id === stateId ? updated : s)),
        );
      });
      track("request_state_updated", { state_id: stateId, fields, source });
      return updated;
    } catch (error) {
      runInAction(() => {
        this.setStatuses(slug, snapshot);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.mutatingStateId = null;
      });
    }
  }

  async deleteStatus(
    slug: string,
    stateId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const snapshot = this.getStatuses(slug);
    runInAction(() => {
      this.mutatingStateId = stateId;
      this.setStatuses(
        slug,
        snapshot.filter((s) => s.id !== stateId),
      );
    });
    try {
      await this.rootStore.requestStatusService.remove(slug, stateId);
      track("request_state_deleted", { state_id: stateId, source });
    } catch (error) {
      runInAction(() => {
        this.setStatuses(slug, snapshot);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.mutatingStateId = null;
      });
    }
  }

  async markDefault(
    slug: string,
    stateId: string,
    source: AnalyticsSource = "page",
  ): Promise<TRequestStatus> {
    const snapshot = this.getStatuses(slug);
    runInAction(() => {
      this.mutatingStateId = stateId;
      this.setStatuses(
        slug,
        snapshot.map((s) => {
          if (s.id === stateId) return { ...s, default: true };
          if (s.default) return { ...s, default: false };
          return s;
        }),
      );
    });
    try {
      const updated = await this.rootStore.requestStatusService.markDefault(slug, stateId);
      runInAction(() => {
        this.setStatuses(
          slug,
          this.getStatuses(slug).map((s) => (s.id === stateId ? updated : s)),
        );
      });
      track("request_state_updated", { state_id: stateId, fields: ["default"], source });
      return updated;
    } catch (error) {
      runInAction(() => {
        this.setStatuses(slug, snapshot);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.mutatingStateId = null;
      });
    }
  }

  async reorder(
    slug: string,
    stateId: string,
    destinationGroup: TRequestStatusGroup,
    destinationIndex: number,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const snapshot = [...this.getStatuses(slug)];
    const target = snapshot.find((s) => s.id === stateId);
    if (!target) return;

    const grouped = this.getGroupedStatuses(slug);
    const sourceGroup = target.group;
    const isSameGroup = sourceGroup === destinationGroup;

    const withoutSource = grouped[sourceGroup].filter((s) => s.id !== stateId);
    const destBucket = isSameGroup ? withoutSource : [...grouped[destinationGroup]];

    const clampedIndex = Math.max(0, Math.min(destinationIndex, destBucket.length));
    const moved = { ...target, group: destinationGroup };
    destBucket.splice(clampedIndex, 0, moved);

    const resequence = (bucket: TRequestStatus[]): TRequestStatus[] =>
      bucket.map((state, idx) => ({ ...state, sequence: idx + 1 }));

    const nextGrouped: TGroupedRequestStatuses = { ...grouped };
    if (isSameGroup) {
      nextGrouped[destinationGroup] = resequence(destBucket);
    } else {
      nextGrouped[sourceGroup] = resequence(withoutSource);
      nextGrouped[destinationGroup] = resequence(destBucket);
    }

    const flat: TRequestStatus[] = [];
    for (const group of REQUEST_STATUS_GROUPS) {
      flat.push(...nextGrouped[group]);
    }

    runInAction(() => {
      this.setStatuses(slug, flat);
      this.mutatingStateId = stateId;
    });

    try {
      const updatedFromServer = await this.rootStore.requestStatusService.update(slug, stateId, {
        group: destinationGroup,
        sequence: clampedIndex + 1,
      });
      runInAction(() => {
        this.setStatuses(
          slug,
          this.getStatuses(slug).map((s) => (s.id === stateId ? updatedFromServer : s)),
        );
      });
      track("request_states_reordered", {
        state_id: stateId,
        group: destinationGroup,
        regrouped: !isSameGroup,
        source,
      });
    } catch (error) {
      runInAction(() => {
        this.setStatuses(slug, snapshot);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.mutatingStateId = null;
      });
    }
  }
}
